'use client';

import { useCallback, useEffect, useState } from 'react';
import { FunctionSquare, Plus, X } from 'lucide-react';
import { useBlockFunctionStore } from '@/stores/useBlockFunctionStore';
import type { FunctionDef, FunctionParam } from '@/stores/useBlockFunctionStore';
import { useBlockModalFunctionStore } from '@/stores/useBlockModalFunctionStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import IconAdaptive from '@/components/IconAdaptive';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';

type VarType = 'float' | 'String' | 'bool';

const TYPE_CONFIG: Record<VarType, { label: string; color: string }> = {
    float: { label: '数値', color: '#FF1BAC' },
    String: { label: '文字列', color: '#FF4081' },
    bool: { label: '真偽', color: '#da00bd' },
};

const MAX_PARAMS = 4;

const EMPTY_FORM = () => ({ params: [] as FunctionParam[], returnType: null as VarType | null });

const isValidIdentifier = (value: string) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value);

function TypeSymbol({ type }: { type: VarType }) {
    const color = TYPE_CONFIG[type].color;
    if (type === 'float') {
        return (
            <svg width="10" height="10" viewBox="0 0 10 10">
                <circle cx="5" cy="5" r="5" fill={color} />
            </svg>
        );
    } else if (type === 'String') {
        return (
            <svg width="10" height="10" viewBox="0 0 10 10">
                <rect width="10" height="10" fill={color} />
            </svg>
        );
    } else {
        return (
            <svg width="12" height="12" viewBox="0 0 12 12">
                <polygon points="6,0 12,6 6,12 0,6" fill={color} />
            </svg>
        );
    }
}

export default function Function() {
    const { closeModal, setOnConfirm } = useBlockModalStore();
    const { fieldRef } = useBlockModalFunctionStore();
    const { functions, addFunction, updateFunction, removeFunction } = useBlockFunctionStore();

    const [selectedId, setSelectedId] = useState<string | null>(() => {
        const current = fieldRef?.getValue();
        return functions.find((f) => f.name === current)?.id ?? null;
    });

    const [form, setForm] = useState(() => {
        const current = fieldRef?.getValue();
        const match = functions.find((f) => f.name === current);
        return match ? { params: match.params, returnType: match.returnType } : EMPTY_FORM();
    });

    const [newName, setNewName] = useState('');
    const [addError, setAddError] = useState('');

    const selectFn = (fn: FunctionDef) => {
        setSelectedId(fn.id);
        setForm({ params: fn.params, returnType: fn.returnType });
        setAddError('');
    };

    const handleAddParam = () => {
        if (form.params.length >= MAX_PARAMS) return;
        setForm((f) => ({ ...f, params: [...f.params, { id: crypto.randomUUID(), type: 'float' as VarType }] }));
    };

    const handleRemoveParam = (i: number) => {
        setForm((f) => ({ ...f, params: f.params.filter((_, idx) => idx !== i) }));
    };

    const updateParam = (i: number, updates: Partial<FunctionParam>) => {
        setForm((f) => ({ ...f, params: f.params.map((p, idx) => (idx === i ? { ...p, ...updates } : p)) }));
    };

    useEffect(() => {
        if (!selectedId) return;
        updateFunction(selectedId, { params: form.params, returnType: form.returnType });
    }, [form, selectedId]);

    const handleAdd = () => {
        const trimmed = newName.trim();
        if (!trimmed) {
            setAddError('関数名を入力してください');
            return;
        }
        if (!isValidIdentifier(trimmed)) {
            setAddError('関数名は英字またはアンダースコアで始まる半角英数字のみ使用できます');
            return;
        }
        const dup = functions.find((f) => f.name === trimmed);
        if (dup) {
            setAddError('同じ名前の関数が既に存在します');
            return;
        }
        const newFn: FunctionDef = { id: crypto.randomUUID(), name: trimmed, params: [], returnType: null };
        addFunction(newFn);
        setNewName('');
        setAddError('');
        setSelectedId(newFn.id);
        setForm(EMPTY_FORM());
    };

    const handleOk = useCallback(() => {
        if (fieldRef && selectedId) {
            const fn = functions.find((f) => f.id === selectedId);
            if (fn) fieldRef.setFnId(fn.id, fn.name);
        }
        closeModal();
    }, [fieldRef, selectedId, functions, closeModal]);

    useEffect(() => {
        setOnConfirm(handleOk);
    }, [handleOk, setOnConfirm]);

    return (
        <div className="flex flex-col h-full bg-color-block-function-modal-bg rounded-2xl overflow-hidden">
            {/* ヘッダー */}
            <div className="flex items-center px-3 h-[50px] flex-shrink-0 bg-color-block-function-modal-header">
                <span className="mr-2">
                    <IconAdaptive Icon={FunctionSquare} strokeWidth={2} baseSize={32} className="text-white" />
                </span>
                <span className="text-color-white-base font-semibold text-sm">関数の管理</span>
            </div>

            {/* 上段：登録済み関数リスト */}
            <div className="px-4 pt-3 pb-3 border-b border-color-block-function-modal-button-border flex-shrink-0 h-20">
                <p className="text-color-block-action-modal-text-muted text-xs mb-2">関数 選択</p>
                <div className="flex gap-3 flex-wrap items-center">
                    {functions.length === 0 && <p className="text-white/30 text-xs">登録済みの関数がありません</p>}
                    {functions.map((fn) => (
                        <div key={fn.id} className="relative flex-shrink-0">
                            <button
                                onClick={() => selectFn(fn)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    selectedId === fn.id
                                        ? 'bg-color-block-function-modal-header text-white'
                                        : 'bg-color-block-function-modal-button-bg-light text-white/70 hover:text-white'
                                }`}
                            >
                                {fn.name}
                            </button>
                            <button
                                onClick={() => {
                                    removeFunction(fn.id);
                                    if (selectedId === fn.id) {
                                        setSelectedId(null);
                                        setForm(EMPTY_FORM());
                                    }
                                }}
                                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-color-block-variable-modal-button-bg border border-color-block-variable-modal-button-border flex items-center justify-center text-white/40 hover:text-red-400 transition-colors"
                            >
                                <IconAdaptive Icon={X} strokeWidth={2} baseSize={10} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* 中央：編集フォーム */}
            {selectedId && (
                <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-8 min-h-0">
                    {/* 関数名編集 */}
                    <div className="flex flex-col gap-2">
                        <p className="text-color-block-action-modal-text-muted text-xs">関数名</p>
                        <input
                            value={functions.find((f) => f.id === selectedId)?.name ?? ''}
                            inputMode="url"
                            onChange={(e) => {
                                const val = e.target.value;
                                if (!val) return;
                                const dup = functions.find((f) => f.name === val && f.id !== selectedId);
                                if (!dup) updateFunction(selectedId, { name: val });
                            }}
                            className="bg-color-block-variable-modal-button-bg text-color-white-base border border-white/20 rounded px-3 py-1.5 text-sm outline-none focus:border-color-block-function-modal-header w-full"
                        />
                    </div>

                    {/* 引数 */}
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <p className="text-color-block-action-modal-text-muted text-xs">
                                引数（最大{MAX_PARAMS}つ）
                            </p>
                            {form.params.length < MAX_PARAMS && (
                                <button
                                    onClick={handleAddParam}
                                    className="flex items-center gap-1 text-xs text-white/50 hover:text-color-block-function-modal-header transition-colors"
                                >
                                    <IconAdaptive Icon={Plus} strokeWidth={2} baseSize={12} />
                                    引数を追加
                                </button>
                            )}
                        </div>
                        <div className="flex gap-2 flex-wrap h-8">
                            {form.params.length === 0 && <p className="text-white/30 text-xs">引数なし</p>}
                            {form.params.map((p, i) => (
                                <div
                                    key={p.id}
                                    className="flex items-center gap-1.5 bg-color-block-variable-modal-button-bg rounded-lg px-2 py-1.5 flex-shrink-0"
                                >
                                    <TypeSymbol type={p.type} />
                                    <select
                                        value={p.type}
                                        onChange={(e) => updateParam(i, { type: e.target.value as VarType })}
                                        className="bg-color-block-variable-modal-button-bg text-color-white-base text-xs outline-none rounded px-1"
                                    >
                                        <option value="float" style={{ backgroundColor: '#1e293b', color: 'white' }}>
                                            数値
                                        </option>
                                        <option value="String" style={{ backgroundColor: '#1e293b', color: 'white' }}>
                                            文字列
                                        </option>
                                        <option value="bool" style={{ backgroundColor: '#1e293b', color: 'white' }}>
                                            真偽
                                        </option>
                                    </select>
                                    <button
                                        onClick={() => handleRemoveParam(i)}
                                        className="text-white/40 hover:text-white/80 transition-colors flex-shrink-0"
                                    >
                                        <IconAdaptive Icon={X} strokeWidth={2} baseSize={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* 戻り値 */}
                    <div className="flex flex-col gap-2">
                        <p className="text-color-block-action-modal-text-muted text-xs">戻り値</p>
                        <div className="flex gap-2 flex-wrap">
                            {([null, 'float', 'String', 'bool'] as const).map((t) => (
                                <button
                                    key={String(t)}
                                    onClick={() => setForm((f) => ({ ...f, returnType: t }))}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                        form.returnType === t
                                            ? 'text-white ring-1 ring-white/60'
                                            : 'bg-color-block-variable-modal-button-bg text-white/50 hover:text-white'
                                    }`}
                                    style={
                                        form.returnType === t
                                            ? { backgroundColor: t ? TYPE_CONFIG[t].color : '#555' }
                                            : {}
                                    }
                                >
                                    {t === null ? 'なし' : TYPE_CONFIG[t].label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* フォーム非表示時のスペーサー */}
            {!selectedId && <div className="flex-1" />}

            {/* フッター */}
            <div className="flex justify-between items-start gap-2 px-5 py-3 border-t border-color-block-variable-modal-button-border flex-shrink-0">
                <div className="flex flex-col gap-1">
                    <div className="flex gap-2 items-center">
                        <input
                            className="bg-color-block-variable-modal-button-bg text-color-white-base border border-white/20 rounded px-3 py-1.5 text-sm outline-none focus:border-color-block-function-modal-header placeholder:text-white/30 w-36"
                            placeholder="関数名"
                            value={newName}
                            inputMode="url"
                            onChange={(e) => {
                                setNewName(e.target.value);
                                setAddError('');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAdd();
                            }}
                        />
                        <button
                            onClick={handleAdd}
                            className={`flex items-center gap-1 px-3 py-1.5 bg-color-block-variable-modal-button-bg border border-dashed border-white/30 rounded-lg text-white/50 text-xs hover:border-color-block-function-modal-header hover:text-color-block-function-modal-header transition-colors flex-shrink-0 ${
                                newName.length > 0 ? 'animate-pulse' : ''
                            }`}
                            style={
                                newName.length > 0
                                    ? { borderColor: CATEGORIES.func.color, color: CATEGORIES.func.color }
                                    : {}
                            }
                        >
                            <IconAdaptive Icon={Plus} strokeWidth={2} baseSize={14} className="mr-1" />
                            関数を追加
                        </button>
                    </div>
                    {addError && <p className="text-red-400 text-xs">{addError}</p>}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                    <button
                        onClick={closeModal}
                        className="px-4 py-2 bg-color-block-variable-modal-cancel text-white/70 rounded-lg text-sm"
                    >
                        キャンセル
                    </button>
                    <button
                        onClick={handleOk}
                        className="px-6 py-2 bg-color-block-function-modal-ok text-color-white-base rounded-lg text-sm font-semibold hover:opacity-80 transition-opacity"
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
}
